import {
  CcfBox,
  CcfDatePickerIcon,
  CcfMinusIcon,
  CcfPlusIcon,
  CcfTextField,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import styles from './simulator-v2-overview-tab.module.scss';
import { useEffect, useState } from 'react';
import { getSelectedContact } from '../../+state/simulatorV2.slice';
import { useSelector } from 'react-redux';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import 'dayjs/locale/en'; // Replace 'en' with the desired locale
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Button } from '@mui/material';
import { generateDate } from '../../utils/utils';

/* eslint-disable-next-line */
export interface SimulatorV2OverviewTabProps {
  setUpdatedContact: any;
  updatedContact: any;
  notesErrors: any;
  setNotesErrors: any;
}

/**
 * used to show the Overview Tab
 * @param props - SimulatorV2OverviewTabProps
 * @example ` <SimulatorV2OverviewTab />`
 */
export function SimulatorV2OverviewTab(props: SimulatorV2OverviewTabProps) {
  const { setUpdatedContact, updatedContact, notesErrors, setNotesErrors } =
    props;
  const selectedContact: any = useSelector(getSelectedContact);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const locale = 'en'; // Set the locale explicitly
  dayjs.locale(locale); // Set the locale globally
  const [selectedNoteNumber, setSelectedNoteNumber] = useState(
    selectedContact.notes[0].notesId
  );
  const [selectedNote, setSelectedNote] = useState<any>(
    selectedContact &&
      Object.keys(selectedContact).length > 0 &&
      selectedContact.notes &&
      selectedContact.notes.length
      ? [selectedContact.notes[0]]
      : []
  );
  const [notes, setNotes] = useState(
    selectedContact &&
      Object.keys(selectedContact).length > 0 &&
      selectedContact.notes &&
      selectedContact.notes.length
      ? selectedContact.notes.slice(0, 3)
      : []
  );

  /**@example */
  // Define a type for a note object
  interface Note {
    noteTitle?: string;
    subTitle?: string;
    agentName?: string;
    [key: string]: any;
  }
  /**
   * Validates that required fields in the note at the given noteNumber are not empty.
   * @param noteNumber - index of the note to validate
   * @example
   * @returns true if all required fields are filled, false otherwise
   */
  const validateJourneyDetails = (noteNumber: number) => {
    const newErrors: { [key: string]: string } = {};
    let valid = true;

    if (Array.isArray(notes) && notes[noteNumber]) {
      const requiredFields = [
        { field: 'noteTitle', label: 'Note Title' },
        { field: 'subTitle', label: 'Note Subtitle' },
        { field: 'agentName', label: 'Agent Name' },
      ];
      const note = notes[noteNumber];

      requiredFields.forEach(({ field, label }) => {
        const errorKey = `notes_${field}_${noteNumber}`;
        if (
          !note?.[field] ||
          (typeof note[field] === 'string' && note[field].trim() === '')
        ) {
          newErrors[errorKey] = `${label} is required in note ${
            noteNumber + 1
          }`;
          valid = false;
          setErrors((prev) => ({ ...prev, ...newErrors }));
        } else {
          // Remove error if field is now valid
          setErrors((prev) => {
            const updated = { ...prev };
            delete updated[errorKey];
            return updated;
          });
        }
      });
    }

    return valid;
  };

  /**
   * Used to display date picker icon
   * @example - DatePickerIcon()
   */
  const DatePickerIcon = () => {
    return (
      <CcfBox sx={{ display: 'flex' }}>
        <CcfDatePickerIcon />
      </CcfBox>
    );
  };

  useEffect(() => {
    setUpdatedContact({
      ...updatedContact,
      lastUpdatedDate: generateDate(),
      notes: [
        ...notes,
        ...selectedContact.notes.slice(3, selectedContact.notes.length),
      ],
    });
  }, [notes]);
  return (
    <div className={styles['container']}>
      <CcfBox
        style={{
          padding: '20px',
          paddingBottom: '0px',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
        }}
      >
        <CcfTypography className={styles.headerText}>
          Overview Tab Details
        </CcfTypography>
        <CcfBox className={styles.subTitleContainer}>
          <CcfTypography
            className={styles.subTitle}
          >{`number of notes (up to ${notes.length})`}</CcfTypography>
          <div style={{ width: '30%', display: 'flex' }}>
            <input
              type="number"
              className={styles.numericSelectorInput}
              step="1"
              min="0"
              max={notes.length - 1}
              value={parseInt(`${selectedNoteNumber + 1}`)}
            />
            <div className={styles.numericSelectorIncGrid}>
              <div className={styles.columnForIncDec}>
                <Button
                  onClick={() => {
                    if (
                      selectedNoteNumber > 0 &&
                      validateJourneyDetails(selectedNoteNumber)
                    ) {
                      const newNoteNumber = selectedNoteNumber - 1;
                      setSelectedNoteNumber(newNoteNumber);
                      setSelectedNote([notes[newNoteNumber]]);
                    }
                  }}
                >
                  <CcfMinusIcon
                    style={{ fontSize: '10px', marginTop: '5px' }}
                  />
                </Button>
              </div>
              <div className={styles.columnForIncDec}>
                <Button
                  onClick={() => {
                    if (
                      selectedNoteNumber < notes.length - 1 &&
                      validateJourneyDetails(selectedNoteNumber)
                    ) {
                      const newNoteNumber = selectedNoteNumber + 1;
                      setSelectedNoteNumber(newNoteNumber);
                      setSelectedNote([notes[newNoteNumber]]);
                    }
                  }}
                >
                  <CcfPlusIcon style={{ fontSize: '10px' }} />
                </Button>
              </div>
            </div>
          </div>
        </CcfBox>
      </CcfBox>
      <CcfBox style={{ padding: '20px 20px 0px', width: '25rem' }}>
        {selectedNote?.map((note: any, index: number) => (
          <CcfBox key={index}>
            <CcfTypography className={styles.noteText}>{`Note ${parseInt(
              `${selectedNoteNumber + 1}`
            )}`}</CcfTypography>
            <CcfBox className={styles.parentGrid}>
              <CcfTypography className={styles.inputLabel}>
                Note Title <span style={{ color: '#C2041A' }}>*</span>
              </CcfTypography>
              <CcfTextField
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: '36px',
                    border: '1px solid #AFC1CE',
                  },
                }}
                error={
                  !!errors[`notes_noteTitle_${selectedNoteNumber}`] ||
                  notesErrors[`notes_noteTitle_${selectedNoteNumber}`]
                }
                helperText={
                  errors[`notes_noteTitle_${selectedNoteNumber}`] ||
                  notesErrors[`notes_noteTitle_${selectedNoteNumber}`]
                }
                color="primary"
                id="sm_overviewTab_notes_title"
                style={{ marginBottom: '10px', marginTop: '5px' }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ classes: { input: styles.inputProps } }}
                value={note.noteTitle}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const updatedData = [...notes];
                  updatedData[selectedNoteNumber] = {
                    ...updatedData[selectedNoteNumber],
                    noteTitle: event?.target.value,
                  };

                  setNotes(updatedData);
                  setSelectedNote([updatedData[selectedNoteNumber]]);
                }}
              />
            </CcfBox>
            <CcfBox className={styles.parentGrid}>
              <CcfTypography className={styles.inputLabel}>
                Note Subtitle <span style={{ color: '#C2041A' }}>*</span>
              </CcfTypography>

              <CcfTextField
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: '36px',
                    border: '1px solid #AFC1CE',
                  },
                }}
                color="primary"
                id="sm_overviewTab_notes_subTitle"
                error={
                  !!errors[`notes_subTitle_${selectedNoteNumber}`] ||
                  notesErrors[`notes_subTitle_${selectedNoteNumber}`]
                }
                helperText={
                  errors[`notes_subTitle_${selectedNoteNumber}`] ||
                  notesErrors[`notes_subTitle_${selectedNoteNumber}`]
                }
                style={{ marginBottom: '10px', marginTop: '5px' }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ classes: { input: styles.inputProps } }}
                value={note.subTitle}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const updatedData = [...notes];
                  updatedData[selectedNoteNumber] = {
                    ...updatedData[selectedNoteNumber],
                    subTitle: event?.target.value,
                  };

                  setNotes(updatedData);
                  setSelectedNote([updatedData[selectedNoteNumber]]);
                }}
              ></CcfTextField>
            </CcfBox>
            <CcfBox className={styles.parentGrid}>
              <CcfTypography className={styles.inputLabel}>
                Note Content
              </CcfTypography>
              <CcfBox className={styles.editBox}>
                <textarea
                  id="editNote"
                  style={{
                    borderRadius: '4px',
                    height: '80px',
                    border: '2px solid #AFC1CE',
                  }}
                  className={styles.note_textarea}
                  value={note.noteContent}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const updatedData = [...notes];
                    updatedData[selectedNoteNumber] = {
                      ...updatedData[selectedNoteNumber],
                      noteContent: event?.target.value,
                    };

                    setNotes(updatedData);
                    setSelectedNote([updatedData[selectedNoteNumber]]);
                  }}
                ></textarea>
              </CcfBox>
            </CcfBox>
            <CcfBox className={styles.parentGrid}>
              <CcfTypography className={styles.inputLabel}>Date</CcfTypography>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={locale}
                id="sm_overviewTab_date"
              >
                <CcfBox data-testid="date-input">
                  <DateTimePicker
                    value={note.date ? dayjs(note.date) : dayjs()}
                    onChange={(event: any) => {
                      const updatedData = [...notes];
                      updatedData[selectedNoteNumber] = {
                        ...updatedData[selectedNoteNumber],
                        date: new Date(event).toISOString(),
                      };

                      setNotes(updatedData);
                      setSelectedNote([updatedData[selectedNoteNumber]]);
                    }}
                    renderInput={(props: any) => (
                      <CcfTextField
                        variant="outlined"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                            height: '36px',
                            border: '1px solid #AFC1CE',
                          },
                        }}
                        style={{
                          marginBottom: '10px',
                          marginTop: '5px',
                          width: '25rem',
                        }}
                        color="primary"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ classes: { input: styles.inputProps } }}
                        id="date"
                        {...props}
                      />
                    )}
                    components={{
                      OpenPickerIcon: DatePickerIcon,
                    }}
                  />
                </CcfBox>
              </LocalizationProvider>
            </CcfBox>
            <CcfBox className={styles.parentGrid}>
              <CcfTypography className={styles.inputLabel}>
                Agent Name <span style={{ color: '#C2041A' }}>*</span>
              </CcfTypography>
              <CcfTextField
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: '36px',
                    border: '1px solid #AFC1CE',
                  },
                }}
                error={
                  !!errors[`notes_agentName_${selectedNoteNumber}`] ||
                  notesErrors[`notes_agentName_${selectedNoteNumber}`]
                }
                helperText={
                  errors[`notes_agentName_${selectedNoteNumber}`] ||
                  notesErrors[`notes_agentName_${selectedNoteNumber}`]
                }
                color="primary"
                InputLabelProps={{ shrink: true }}
                InputProps={{ classes: { input: styles.inputProps } }}
                id="sm_overviewTab_agentName"
                style={{ marginBottom: '15px', marginTop: '5px' }}
                value={note.agentName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const updatedData = [...notes];
                  updatedData[selectedNoteNumber] = {
                    ...updatedData[selectedNoteNumber],
                    agentName: event?.target.value,
                  };

                  setNotes(updatedData);
                  setSelectedNote([updatedData[selectedNoteNumber]]);
                }}
              ></CcfTextField>
            </CcfBox>
          </CcfBox>
        ))}
      </CcfBox>
    </div>
  );
}

export default SimulatorV2OverviewTab;
